import {createContext, useContext} from 'react'
import {ParsedUrlQuery} from 'querystring'
import {UrlObject} from 'url'

export type Url = UrlObject | string

export type QueryParams = ParsedUrlQuery | {[key: string]: unknown}

type RouterType = 'next' | 'react-router'

export interface RouterContextProps {
  /**
   * A function that pushes a new URL to the history stack
   * @param url - The URL to push
   */
  push: (url: Url) => void
  /**
   * A function that replaces the current URL in the history stack
   * @param url - The URL to replace
   */
  replace: (url: Url) => void
  /**
   * A function that navigates back in the history stack
   */
  goBack: () => void
  /**
   * A function that returns the query parameters of the current URL
   * @returns The query parameters
   */
  getQueryParams: () => QueryParams
  /**
   * The current URL query parameter string (URL search string)
   */
  queryString: string
  /**
   * The current URL pathname, without the query string
   */
  pathname: string
  /**
   * The current active router type - only used for useParams
   */
  routerType: RouterType
}

/**
 * This context is used to provide the router context to the app.
 * It is used to navigate the app and get the query parameters of the current URL.
 *
 * This is a temporary context to facilitate the migration to Next.js.
 * It should be deleted as we fully migrate the app to the framework.
 */
export const RouterContext = createContext<RouterContextProps>(
  {} as RouterContextProps
)

/**
 * Hook to get the router context
 * @returns The router context
 */
export const useRouter = () => useContext(RouterContext)
